<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { SuiButton, SuiDropdown, SuiForm, useToast } from 'vue-fomantic-ui'
import { useContainerStore } from '@/stores/containers'
import { useContainerTypeStore } from '@/stores/container_types'
import { useRoomStore } from '@/stores/rooms'
import type { Container } from '@/models/Container'
import Title from '@/components/Title.vue'
import type { ContainerType } from '@/models/ContainerType'
import type { Room } from '@/models/Room'

const props = defineProps({
  code: {
    type: [String],
    required: true,
  },
})

const router = useRouter()
const { toast } = useToast()

const containerStore = useContainerStore()
const containerTypeStore = useContainerTypeStore()
const roomStore = useRoomStore()

const container = inject('container') as Ref<Container | null>

/* Dropdowns */

const selectedWasteType = ref()
const selectedVolume = ref()

const wasteTypes = containerTypeStore.wasteTypes.map((type) => ({
  text: type.name,
  value: type,
}))
const volumes = computed(() => {
  return containerTypeStore.getVolumes(selectedWasteType.value?.value).map((volume) => ({
    text: `${volume} L`,
    value: volume,
  }))
})

watch(selectedWasteType, () => {
  removeError('wasteType')

  if (
    selectedVolume.value &&
    !selectedWasteType.value.value.volumes.includes(selectedVolume.value.value)
  ) {
    selectedVolume.value = null
  }
})

watch(selectedVolume, () => removeError('volume'))

/* Form validation */

const errors: Ref<string[]> = ref([])

const removeError = (error: string) => {
  if (errors.value.includes(error)) {
    //errors.value = errors.value.filter(e => e !== error)
    errors.value.splice(errors.value.indexOf(error), 1)
  }
}

const checkForm = () => {
  errors.value = []

  if (!selectedWasteType.value) {
    errors.value.push('wasteType')
  }

  if (!selectedVolume.value) {
    errors.value.push('volume')
  }

  return errors.value.length === 0
}

/* Save */

const saving = ref(false)

const save = async () => {
  if (!checkForm()) return

  const containerType = containerTypeStore.findByWasteTypeAndVolume(
    selectedWasteType.value.value,
    selectedVolume.value.value,
  )
  saving.value = true

  container.value = await containerStore.addContainer(
    props.code,
    containerType as ContainerType,
    roomStore.defaultRoom as Room,
  )

  toast({
    position: 'centered',
    message: `Le bac ${props.code} a été créé.`,
    type: 'success',
  })

  router.push({ name: 'weigh-container-scan' })
}
</script>

<template>
  <div class="page weigh-container-unknown">
    <Title>Bac inconnu</Title>
    <SuiForm @submit.prevent="save">
      <p>Vous venez de scanner un bac inconnu :</p>
      <p class="code">{{ props.code }}</p>
      <p>Vous avez la possibilité de créer un nouveau bac pour ce code barre.</p>
      <div :class="{ field: true, 'waste-type': true, error: errors.includes('wasteType') }">
        <label>Type de bac</label>
        <SuiDropdown
          :options="wasteTypes"
          v-model="selectedWasteType"
          placeholder="Type de bac"
          selection
        />
      </div>
      <div :class="{ field: true, volume: true, error: errors.includes('volume') }">
        <label>Contenance du bac</label>
        <SuiDropdown
          :options="volumes"
          v-model="selectedVolume"
          placeholder="Contenance"
          selection
        />
      </div>
      <SuiButton class="submit" type="submit" :loading="saving">Créer le bac</SuiButton>
    </SuiForm>
  </div>
</template>

<style scoped lang="scss">
.weigh-container-unknown {
  p {
    width: 100%;
    text-align: left;
    margin: 1.25vh 0;
  }

  .code {
    font-size: 20px;
    font-weight: bold;
    text-align: center;
  }

  .field {
    width: 100%;

    &.waste-type {
      margin-top: 2dvh;
    }
  }

  .submit {
    margin-top: 3dvh;
  }
}

/* @TODO: override Fomantic styles in a cleaner way */
.ui.form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 90%;
  padding: 2dvh 0;
}
.ui.form .field > label {
  display: block;
  margin: 0 0 0.28571429rem 0;
  font-size: 13px;
  font-weight: 500;
  text-transform: none;
}
.ui.form:not(.inverted) .field > label:not(.button) {
  color: #344c60;
}
.ui.button {
  font-family: 'Libre Franklin', sans-serif;
  padding: 8px 20px 7px 20px;
  background-color: #344c60;
  transition: opacity 0.25s;
  color: white;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0;
  height: 40px;
  width: 60%;
  line-height: 22px;
  margin-top: auto;
  margin-bottom: 20px;
}
</style>
